import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal, notification, Skeleton, Space, Statistic, Typography } from 'antd';
import CircleExclamation from 'assets/Images/circle-exclamation';
import dayjs, { Dayjs } from 'dayjs';
import { klwAxios, setKringloopwijzerId } from 'helpers/api';
import { atoms } from 'helpers/atoms';
import { useLogout } from 'helpers/hooks/use-logout';
import { refreshCallContext, useGetCallContext } from 'helpers/queries/call-context';
import { useAtom } from 'jotai';
import { JSX, useEffect, useRef, useState } from 'react';

const updateToken = (token: string | undefined) => (klwAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`);
const MINUTES = 1000 * 60;
const { Title } = Typography;
const { Countdown } = Statistic;

const getRefreshTokenExpiration = (keycloak: Keycloak.KeycloakInstance): Dayjs | undefined => {
  const expiration = keycloak.refreshTokenParsed?.exp;
  return expiration ? dayjs.unix(expiration) : undefined;
};

export const AuthWrapper = ({ children }: any): JSX.Element => {
  const [isInitializing, setInitializing] = useState(true);
  const [path, setPath] = useState<string>(window.location.pathname);
  const [keycloak, setKeycloakInstance] = useAtom(atoms.auth.keycloakInstance);
  const [expTime, setExp] = useState<Dayjs>();
  const [showModal, setModal] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const logout = useLogout();
  const didInitKeycloak = useRef(false);
  useGetCallContext(!isInitializing && keycloak.authenticated && !!keycloak.token);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentPath = window.location.pathname;
      if (path !== currentPath) {
        setPath(currentPath);
      }
    }, 1000 * 60);

    return () => clearInterval(interval);
  }, [path]);

  const logoutLogic = (reason: string) => {
    console.log('logging out: ' + reason);
    void logout();
  };

  useEffect(() => {
    const wasRefresh = !!sessionStorage.getItem('klw_reloading');
    const hadSession = !!localStorage.getItem('klw_was_active');

    if (wasRefresh) {
      sessionStorage.removeItem('klw_reloading');
    } else if (hadSession) {
      setKringloopwijzerId(undefined);
      localStorage.clear();
      klwAxios.defaults.headers.common['Kvk-Proxy'] = undefined;
      queryClient.clear();
    }
    localStorage.setItem('klw_was_active', '1');

    const handleBeforeUnload = () => {
      sessionStorage.setItem('klw_reloading', '1');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const warningInMinutes = 5 * MINUTES;
  const timeOutTime = expTime ? expTime.diff() - warningInMinutes : undefined;
  useEffect(() => {
    const refreshTokenExpiration = getRefreshTokenExpiration(keycloak);
    if (refreshTokenExpiration && refreshTokenExpiration.diff() < 15 * MINUTES) {
      keycloak.updateToken(4 * 60 * 60).catch(() => {
        notification.info({ message: 'uw sessie is verlopen log alstublieft opnieuw in' });
        logoutLogic(`sessie is verlopen, time difference ${refreshTokenExpiration.diff()}`);
      });
    }
  }, [path]);

  useEffect(() => {
    if (keycloak.authenticated && timeOutTime !== undefined) {
      const timeout = setTimeout(() => {
        setModal(true);
      }, timeOutTime);
      return () => clearTimeout(timeout);
    }
  }, [keycloak, timeOutTime, expTime]);

  // #region keycloak eventhandlers
  keycloak.onAuthSuccess = () => {
    console.log('auth success');
    updateToken(keycloak.token);
    setExp(getRefreshTokenExpiration(keycloak));
    void refreshCallContext(queryClient);
  };

  keycloak.onAuthRefreshSuccess = () => {
    console.log('auth refresh success');
    updateToken(keycloak.token);
    setExp(getRefreshTokenExpiration(keycloak));
  };
  keycloak.onAuthError = error => console.error('Keycloak onAuthError:', error);
  keycloak.onAuthRefreshError = () => console.error('Keycloak onAuthRefreshError');
  keycloak.onTokenExpired = () => {
    keycloak
      .updateToken(60)
      .then(refreshed => {
        if (refreshed) {
          setExp(getRefreshTokenExpiration(keycloak));
        }
      })
      .catch(() => console.error('Failed to refresh token ' + new Date()));
  };
  // #endregion

  useEffect(() => {
    if (didInitKeycloak.current) return;
    didInitKeycloak.current = true;
    keycloak
      .init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        checkLoginIframe: false,
      })
      .then(authenticated => {
        if (authenticated) updateToken(keycloak.token);
        setKeycloakInstance(keycloak);
        setInitializing(false);
      })
      .catch();
  }, [keycloak, setKeycloakInstance]);

  return (
    <>
      <Skeleton loading={isInitializing}>
        <span>{children}</span>
        <Modal open={showModal} destroyOnHidden maskClosable={false} closable={false} width={400} footer={false}>
          <div style={{ textAlign: 'center' }}>
            <CircleExclamation color="orange" style={{ height: 60, marginBottom: 9 }} />
            <Title level={3}>Uw sessie verloopt!</Title>
            <Typography>
              Uw sessie verloopt over{' '}
              <Countdown
                value={expTime?.valueOf()}
                format="mm:ss"
                onFinish={() => {
                  if (showModal) {
                    logoutLogic('timer in modal hit 0');
                  }
                }}
              />
              Vervolg uw sessie of log uit
            </Typography>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <Space>
                <Button
                  type="primary"
                  onClick={() => {
                    keycloak
                      .updateToken(4 * 60 * 60)
                      .then(refreshed => {
                        if (refreshed) {
                          setModal(false);
                        } else console.log('didnt refresh');
                      })
                      .finally(() => {
                        setModal(false);
                      });
                  }}>
                  Vervolg sessie
                </Button>
                <Button
                  /* @ts-ignore */
                  type="dark"
                  onClick={() => {
                    logoutLogic('logout button pressed in modal');
                  }}>
                  Log uit
                </Button>
              </Space>
            </div>
          </div>
        </Modal>
      </Skeleton>
    </>
  );
};
